/**
 * usePhotoImage — fetches a committed vehicle photo as an authenticated blob URL.
 *
 * Mirrors useDocumentImage but targets GET /v1/photos/:id/raw instead of the
 * document-content endpoint. The object URL is revoked on unmount / photoId change.
 */

import { env } from '@/lib/env';
import { auth } from '@/lib/firebase';
import { useEffect, useState } from 'react';

export type PhotoImageState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; url: string }
  | { status: 'not_found' }
  | { status: 'error'; message: string };

/**
 * Fetches a vehicle photo from /v1/photos/:id/raw (owner-authed).
 * Lazy-loads: only fires when the tile approaches the viewport (controlled by
 * the `enabled` flag, driven by IntersectionObserver in PhotoTile).
 */
export function usePhotoImage(photoId: string | null | undefined, enabled = true): PhotoImageState {
  const [state, setState] = useState<PhotoImageState>({ status: 'idle' });

  useEffect(() => {
    if (!photoId || !enabled) {
      setState({ status: 'idle' });
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    setState({ status: 'loading' });

    async function fetchImage() {
      const user = auth.currentUser;
      if (!user) {
        if (!cancelled) setState({ status: 'error', message: 'Not signed in' });
        return;
      }

      const token = await user.getIdToken();
      const endpoint = `${env.VITE_GG_API_BASE_URL}/v1/photos/${encodeURIComponent(photoId!)}/raw`;

      let res: Response;
      try {
        res = await fetch(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        if (!cancelled) setState({ status: 'error', message: 'Network error fetching photo' });
        return;
      }

      if (res.status === 404) {
        if (!cancelled) setState({ status: 'not_found' });
        return;
      }

      if (!res.ok) {
        if (!cancelled)
          setState({ status: 'error', message: `Could not load photo (${res.status})` });
        return;
      }

      let blob: Blob;
      try {
        blob = await res.blob();
      } catch {
        if (!cancelled) setState({ status: 'error', message: 'Could not read photo data' });
        return;
      }

      objectUrl = URL.createObjectURL(blob);
      if (!cancelled) {
        setState({ status: 'ready', url: objectUrl });
      } else {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    }

    void fetchImage();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };
  }, [photoId, enabled]);

  return state;
}
